import { useRef, useState } from "react";

type Side = "left" | "right";
interface Focus {
  id: string;
  side: Side;
  token: number;
}

// Shared left -> right chaining for pairs of <KeyPicker>s. After a player's left
// key is set we want their right picker to enter selection mode on its own. A
// KeyPicker auto-selects when its `autoSelectToken` prop changes to a new truthy
// value, so we hand each picker a token via `focusToken(id, side)` and bump it
// from `advance(id, side)` once a key has been picked.
export function useKeyChainFocus() {
  const [focus, setFocus] = useState<Focus | null>(null);
  const tokenRef = useRef(0);

  // Call right after a key was picked for (id, side). Setting "left" arms the
  // matching right picker; setting "right" ends the chain.
  function advance(id: string, side: Side) {
    if (side === "left") {
      tokenRef.current += 1;
      setFocus({ id, side: "right", token: tokenRef.current });
    } else {
      setFocus(null);
    }
  }

  // The `autoSelectToken` to pass to the picker for (id, side): a fresh non-zero
  // value when it's that picker's turn to auto-select, otherwise 0 (no-op).
  function focusToken(id: string, side: Side) {
    return focus && focus.id === id && focus.side === side ? focus.token : 0;
  }

  return { advance, focusToken };
}
